/** Bounded PCM preview. Final recording and action submission are independent. */
const RATE=16000, CAPACITY=RATE*12;
export type PreviewState={text:string;status:'idle'|'listening'|'transcribing'|'unavailable'};
export function createPreviewBuffer(){
  const ring=new Float32Array(CAPACITY);let length=0,index=0,phase=0,sum=0;
  return {
    append(input:Float32Array,rate:number){
      if(!Number.isFinite(rate)||rate<8000||rate>192000)return;
      for(const value of input){
        let weight=RATE/rate;
        while(weight>1e-10){
          const part=Math.min(1-phase,weight);sum+=(Number.isFinite(value)?value:0)*part;phase+=part;weight-=part;
          if(phase>=1-1e-10){ring[index]=sum;index=(index+1)%CAPACITY;length=Math.min(CAPACITY,length+1);phase=0;sum=0;}
        }
      }
    },
    samples:()=>length,
    clear(){length=0;index=0;phase=0;sum=0;},
    wav(){
      const bytes=new ArrayBuffer(44+length*2),view=new DataView(bytes);
      const word=(offset:number,text:string)=>{for(let i=0;i<text.length;i++)view.setUint8(offset+i,text.charCodeAt(i));};
      word(0,'RIFF');view.setUint32(4,36+length*2,true);word(8,'WAVE');word(12,'fmt ');
      view.setUint32(16,16,true);view.setUint16(20,1,true);view.setUint16(22,1,true);
      view.setUint32(24,RATE,true);view.setUint32(28,RATE*2,true);view.setUint16(32,2,true);view.setUint16(34,16,true);
      word(36,'data');view.setUint32(40,length*2,true);
      const start=(index-length+CAPACITY)%CAPACITY;
      for(let i=0;i<length;i++){const sample=Math.max(-1,Math.min(1,ring[(start+i)%CAPACITY]!));view.setInt16(44+i*2,sample*(sample<0?32768:32767),true);}
      return new Blob([bytes],{type:'audio/wav'});
    },
  };
}
export function createLivePreview(deps:{transcribe(audio:Blob,signal:AbortSignal):Promise<{text:string}>;onUpdate(state:PreviewState):void;now?:()=>number}){
  const buffer=createPreviewBuffer(),now=deps.now??Date.now;
  let active=false,closed=false,dirty=false,available=true,generation=0,lastStart=-Infinity,text='';
  let request:AbortController|null=null;
  const update=(status:PreviewState['status'])=>deps.onUpdate({text,status});
  const setActive=(value:boolean)=>{if(closed)return;active=value;if(!value){generation++;request?.abort();}update(!available?'unavailable':value?'listening':'idle');};
  const reset=()=>{active=false;generation++;request?.abort();buffer.clear();dirty=false;available=true;lastStart=-Infinity;text='';update('idle');};
  return {
    setActive,reset,
    setAvailable(value:boolean){available=value;if(!value){generation++;request?.abort();update('unavailable');}},
    push(samples:Float32Array,rate:number){
      if(closed||!available)return;buffer.append(samples,rate);
      let energy=0;for(const value of samples)energy+=value*value;
      if(samples.length&&Math.sqrt(energy/samples.length)>=0.008)dirty=true;
    },
    async tick(){
      if(closed||!available||!active||!dirty||request||buffer.samples()<RATE/2||now()-lastStart<3000)return;
      const mine=generation,controller=new AbortController();request=controller;lastStart=now();dirty=false;update('transcribing');
      try{const result=await deps.transcribe(buffer.wav(),controller.signal);
        if(mine===generation&&active&&!closed){text=result.text.trim();update('listening');}
      }catch{if(mine===generation&&active&&!closed){dirty=true;update('unavailable');}}
      finally{if(request===controller)request=null;}
    },
    close(){reset();closed=true;},
  };
}
